/* eslint-disable */
import { Box } from "@mui/material";
import { StriimButton, StriimTypography, StriimFeedbackBlock, StriimTooltip } from "@striim/striim-ui";
import React, { useEffect, useState } from "react";
import type { optionsType } from "src/modules/apps/pages/create-app-page-templates/Components/create-app-page-app-type-dropdown";
import { buildCancelledResponse } from "../utils/hitl-cancel";

const AUTOMATED_PIPELINE_DESCRIPTION = (
    <>
        <strong>Automated pipeline </strong> wizards create linked applications to handle initial data loading and
        continuous, real-time updates via change data capture (CDC). These pipelines automatically manage the handover
        between processes and enable recovery to ensure seamless data replication.
    </>
);

const AUTOMATED_MODE_DESCRIPTION = (
    <>
        wizards create linked applications to handle initial data loading and continuous, real-time updates via change
        data capture (CDC). These pipelines automatically manage the handover between processes and enable recovery to
        ensure seamless data replication.
    </>
);

const APP_TYPE_DESCRIPTIONS: Record<string, { title: string; description: React.ReactNode }> = {
    IL: {
        title: "Initial Load",
        description: (
            <>
                <strong>Initial Load</strong> is the one-time, full extraction and migration of all historical data from
                a source system to a target destination.
            </>
        )
    },
    CDC: {
        title: "Change Data Capture",
        description: (
            <>
                <strong>CDC (Change Data Capture)</strong> is the continuous process of identifying and capturing only
                the subsequent data inserts, updates, and deletes to keep the target system synchronized in real time.
            </>
        )
    },
    ILCDC: {
        title: "Automated Pipeline",
        description: AUTOMATED_PIPELINE_DESCRIPTION
    },
    AUTOMATEDILCDC: {
        title: "Automated Pipeline",
        description: AUTOMATED_PIPELINE_DESCRIPTION
    },
    AUTOMATED: {
        title: "Automated Mode",
        description: AUTOMATED_MODE_DESCRIPTION
    },
    AUTOMATEDMODE: {
        title: "Automated Mode",
        description: AUTOMATED_MODE_DESCRIPTION
    }
};

interface AppTypeDescriptionListProps {
    appTypes: optionsType[];
}

const AppTypeDescriptionList: React.FC<AppTypeDescriptionListProps> = ({ appTypes = [] }) => (
    <StriimTypography variant="body4" color="greyscale.700">
        {appTypes.map(type => {
            const config = APP_TYPE_DESCRIPTIONS[type.value];
            if (!config) return null;
            return <p>{config.description}</p>;
        })}
    </StriimTypography>
);

const AUTOMATED_PIPELINE_VALUES = ["ILCDC", "AUTOMATEDILCDC"];

interface AppTypeButtonProps {
    type: optionsType;
    variant: "primary" | "secondary";
    onSelect?: (value: string) => void;
}

const AppTypeButton: React.FC<AppTypeButtonProps> = ({ type, variant, onSelect }) => {
    const isDisabled = type.isEnabled === false;
    return (
        <StriimTooltip
            title={
                isDisabled
                    ? "Enabling the selected adapter requires additional license, please contact support@striim.com."
                    : null
            }
            arrow
            placement="bottom"
            slotProps={{ tooltip: { sx: { fontFamily: "Inter" } } }}
        >
            <StriimButton
                variant={variant}
                disabled={isDisabled}
                onClick={() => {
                    if (isDisabled) return;
                    onSelect?.(type.value);
                }}
            >
                {type.label}
            </StriimButton>
        </StriimTooltip>
    );
};

export interface AskILOrCDCProps {
    appTypes: optionsType[];
    respond?: (result: string) => void;
}

export const AskILOrCDC: React.FC<AskILOrCDCProps> = ({ appTypes = [], respond }) => {
    // Only enabled (licensed) app types are selectable; unlicensed ones are shown disabled.
    const enabledAppTypes = appTypes.filter(type => type.isEnabled !== false);

    useEffect(() => {
        if (enabledAppTypes.length === 1) {
            // Auto-select the only available app type and tell the skill to continue with it.
            respond?.(
                `The only available app type "${enabledAppTypes[0].value}" has been selected automatically. Proceed to the next step of the skill using this app type.`
            );
        } else if (enabledAppTypes.length === 0) {
            // No app type applies to this source/target — let the skill continue without one.
            respond?.(
                "No app type selection is required for this source/target. Proceed to the next step of the skill without an app type."
            );
        }
    }, [enabledAppTypes, respond]);

    if (enabledAppTypes.length === 1) {
        return null;
    }

    // Automated Pipeline gets its own full-width row; everything else shares the top row.
    // const automatedAppType = appTypes.find(type => AUTOMATED_PIPELINE_VALUES.includes(type.value));
    const standardAppTypes = appTypes.filter(type => !AUTOMATED_PIPELINE_VALUES.includes(type.value));

    const handleCancel = () => {
        respond?.(buildCancelledResponse());
    };

    return (
        <Box
            sx={{
                p: 2,
                border: "1px solid",
                borderColor: "greyscale.200",
                borderRadius: 2,
                mt: 1,
                backgroundColor: "background.paper",
                width: "100%"
            }}
        >
            <StriimTypography
                variant="h3"
                sx={{
                    mb: 1,
                    fontWeight: "bold",
                    color: "text.primary"
                }}
            >
                How would you like to read your data?
            </StriimTypography>

            <AppTypeDescriptionList appTypes={standardAppTypes} />

            <Box
                sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    mt: 2,
                    gap: 1
                }}
            >
                {standardAppTypes.map(type => (
                    <AppTypeButton key={type.value} type={type} variant="secondary" onSelect={respond} />
                ))}
                <StriimButton
                    variant="text"
                    onClick={handleCancel}
                >
                    Cancel
                </StriimButton>
            </Box>
            {/* {automatedAppType && (
                <Box sx={{ mt: 1 }}>
                    <AppTypeButton type={automatedAppType} variant="primary" onSelect={respond} />
                </Box>
            )} */}
        </Box>
    );
};
